if (!customElements.get('vr-product-action')) {
  customElements.define(
    'vr-product-action',
    class VelvetRosaProductAction extends HTMLElement {
      constructor() {
        super();
        this.variantUnsubscriber = null;
        this.ctaObserver = null;
        this.raf = null;
        this.onScroll = this.onScroll.bind(this);
        this.onGiftInput = this.onGiftInput.bind(this);
        this.onQuantityInput = this.onQuantityInput.bind(this);
      }

      connectedCallback() {
        this.variantInput = this.querySelector('input[name="id"]');
        this.quantityInput = this.querySelector('input[name="quantity"]');
        this.giftInput = this.querySelector('input[data-vr-gift-message]');
        this.priceNode = this.querySelector('[data-vr-action-price]');
        this.variantNode = this.querySelector('[data-vr-action-variant]');
        this.imageNode = this.querySelector('[data-vr-action-image]') || this.closest('section')?.querySelector('[data-vr-action-image]');
        const configuredMainSection = this.dataset.mainSection || '';
        const productInfoNodes = Array.from(document.querySelectorAll('product-info[id^="MainProduct-"]'));
        const matchingProductInfo = productInfoNodes.find((node) => node.dataset.section === configuredMainSection) || productInfoNodes[0];
        this.mainSection = matchingProductInfo?.dataset.section || configuredMainSection || 'main';
        this.submitButton = this.querySelector('[type="submit"]');
        this.submitText = this.submitButton?.querySelector('[data-vr-action-button-text]');

        this.syncMainFormExtras();
        this.bindMainFormExtras();
        this.bindVariantEvents();

        if (this.dataset.kind === 'sticky') this.setupStickyVisibility();
      }

      disconnectedCallback() {
        this.variantUnsubscriber?.();
        this.ctaObserver?.disconnect();
        window.removeEventListener('scroll', this.onScroll);
        window.removeEventListener('resize', this.onScroll);
        document.removeEventListener('input', this.onGiftInput);
        document.removeEventListener('change', this.onGiftInput);
        document.removeEventListener('input', this.onQuantityInput);
        document.removeEventListener('change', this.onQuantityInput);
      }

      bindVariantEvents() {
        const subscribeWhenReady = () => {
          if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
            this.variantUnsubscriber = subscribe(PUB_SUB_EVENTS.variantChange, (event) => {
              const variant = event?.data?.variant;
              if (variant) this.updateVariant(variant);
            });
          } else {
            window.setTimeout(subscribeWhenReady, 50);
          }
        };
        subscribeWhenReady();
      }

      bindMainFormExtras() {
        document.addEventListener('input', this.onGiftInput);
        document.addEventListener('change', this.onGiftInput);
        document.addEventListener('input', this.onQuantityInput);
        document.addEventListener('change', this.onQuantityInput);
      }

      onGiftInput(event) {
        if (event.target?.matches?.('.vr-gift-message textarea[name="properties[Gift message]"]')) {
          this.syncGiftMessage();
        }
      }

      onQuantityInput(event) {
        if (event.target?.matches?.('#MainProduct-' + this.mainSection + ' input[name="quantity"]')) {
          this.syncQuantity();
        }
      }

      syncMainFormExtras() {
        this.syncGiftMessage();
        this.syncQuantity();
      }

      syncGiftMessage() {
        if (!this.giftInput) return;
        const source = document.querySelector(
          '#MainProduct-' + this.mainSection + ' .vr-gift-message textarea[name="properties[Gift message]"]'
        );
        this.giftInput.value = source?.value || '';
      }

      syncQuantity() {
        if (!this.quantityInput) return;
        const source = document.querySelector('#MainProduct-' + this.mainSection + ' input[name="quantity"]');
        this.quantityInput.value = source?.value || '1';
      }

      updateVariant(variant) {
        if (this.variantInput) {
          this.variantInput.value = variant.id || '';
          this.variantInput.disabled = !variant.available;
          this.variantInput.dispatchEvent(new Event('change', { bubbles: true }));
        }

        if (this.priceNode && Number.isFinite(Number(variant.price))) {
          const currency = this.dataset.currency || window.Shopify?.currency?.active || 'USD';
          try {
            this.priceNode.textContent = new Intl.NumberFormat(document.documentElement.lang || 'en-US', {
              style: 'currency',
              currency,
            }).format(Number(variant.price) / 100);
          } catch (_) {
            this.priceNode.textContent = (Number(variant.price) / 100).toFixed(2) + ' ' + currency;
          }
        }

        if (this.variantNode && Array.isArray(variant.options)) {
          this.variantNode.textContent = variant.options.filter(Boolean).join(' · ');
        }

        const imageSrc = variant?.featured_media?.preview_image?.src || variant?.featured_media?.preview_image?.url;
        if (this.imageNode && imageSrc) {
          this.imageNode.src = imageSrc;
          this.imageNode.removeAttribute('srcset');
        }

        if (this.submitButton) {
          this.submitButton.disabled = !variant.available;
          this.submitButton.toggleAttribute('aria-disabled', !variant.available);
        }
        if (this.submitText) {
          this.submitText.textContent = variant.available ? this.dataset.addLabel || 'Add to cart' : this.dataset.soldLabel || 'Sold out';
        }

        this.syncMainFormExtras();
      }

      setupStickyVisibility() {
        this.mainButton = document.querySelector('#ProductSubmitButton-' + this.mainSection);
        const cta = document.querySelector('[data-vr-product-cta]');

        if (cta && 'IntersectionObserver' in window) {
          this.ctaObserver = new IntersectionObserver(
            (entries) => {
              this.ctaVisible = entries.some((entry) => entry.isIntersecting && entry.intersectionRatio > 0.15);
              this.updateStickyVisibility();
            },
            { threshold: [0, 0.15, 0.5] }
          );
          this.ctaObserver.observe(cta);
        }

        window.addEventListener('scroll', this.onScroll, { passive: true });
        window.addEventListener('resize', this.onScroll);
        this.updateStickyVisibility();
      }

      onScroll() {
        if (this.raf) return;
        this.raf = window.requestAnimationFrame(() => {
          this.raf = null;
          this.updateStickyVisibility();
        });
      }

      updateStickyVisibility() {
        if (!this.mainButton) {
          this.classList.remove('is-visible');
          return;
        }

        const header = document.querySelector('sticky-header, .shopify-section-group-header-group .header-wrapper');
        const headerBottom = header ? Math.max(0, header.getBoundingClientRect().bottom) : 0;
        this.style.setProperty('--vr-sticky-top', headerBottom + 'px');

        const mainRect = this.mainButton.getBoundingClientRect();
        const passedMainButton = mainRect.bottom <= headerBottom + 8;
        const shouldShow = passedMainButton && !this.ctaVisible;
        this.classList.toggle('is-visible', shouldShow);
      }
    }
  );
}
